import {
  fetchDeploymentsByAppId,
  selectDeploymentById,
  selectDeploymentsByAppId,
} from "@app/deployment";
import { useCompositeLoader, useQuery, useSelector } from "@app/react";
import type { DeployApp, Deployment } from "@app/types";
import { useState } from "react";
import { ButtonSensitive, IconRollback } from "../index";
import { RollbackDeploymentModal } from "./rollback-deployment-modal";
import { RollbackEnvChanges } from "./rollback-env-changes";

const MAX_CANDIDATES = 3;

export function rollbackCandidates(
  deployments: Deployment[],
  currentDeploymentId: string,
): Deployment[] {
  const seen = new Set<string>();

  // Keyed on image *and* configuration: restoring env vars sends the selected
  // deployment's own configurationId, so an env-only redeploy of the same image
  // is a distinct rollback target. `deployments` is newest-first, so the row
  // kept for each pair is that pair's most recent successful deploy.
  return deployments.filter((d) => {
    const key = `${d.imageId}:${d.configurationId}`;

    if (
      d.id === currentDeploymentId ||
      d.status !== "succeeded" ||
      d.imageId === "" ||
      seen.has(key)
    ) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

/**
 * Fetches on mount rather than on open, so the modal always reads settled data.
 * Deliberately does not fetch the current deployment by id: parents watch that
 * loader and swap us out for a spinner when it goes back into flight, which
 * would unmount us mid-fetch and restart the cycle.
 */
export function RollbackButton({ app }: { app: DeployApp }) {
  const [isOpen, setIsOpen] = useState(false);

  const deploymentsAction = fetchDeploymentsByAppId({ id: app.id });
  useQuery(deploymentsAction);
  const deployments = useSelector((s) =>
    selectDeploymentsByAppId(s, { appId: app.id }),
  );
  // The list carries the current deployment too, so there is no second fetch.
  const currentDeployment = useSelector((s) =>
    selectDeploymentById(s, { id: app.currentDeploymentId }),
  );

  const loader = useCompositeLoader([deploymentsAction]);

  // `isInitialLoading` covers the idle window before the query dispatches; the
  // id check is what proves the data arrived, since a composite reports success
  // while a member is still idle.
  const isReady =
    !loader.isLoading &&
    !loader.isInitialLoading &&
    currentDeployment.id !== "";

  const candidates = rollbackCandidates(
    deployments,
    app.currentDeploymentId,
  ).slice(0, MAX_CANDIDATES);

  return (
    <>
      <ButtonSensitive
        envId={app.environmentId}
        variant="white"
        size="sm"
        disabled={!isReady}
        onClick={() => setIsOpen(true)}
      >
        <IconRollback variant="sm" className="mr-1" />
        Rollback
      </ButtonSensitive>

      {isOpen ? (
        <RollbackDeploymentModal
          isOpen
          onClose={() => setIsOpen(false)}
          appName={app.handle}
          currentDeployment={currentDeployment}
          candidates={candidates}
          renderEnvChanges={(targetConfigurationId, onEmptyChange) => (
            <RollbackEnvChanges
              targetConfigurationId={targetConfigurationId}
              currentConfigurationId={currentDeployment.configurationId}
              onEmptyChange={onEmptyChange}
            />
          )}
        />
      ) : null}
    </>
  );
}
